import { z } from "zod";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Briefcase, Calendar } from "lucide-react";
import { EnterpriseCompanyForm } from "./Enterprise/EnterpriseCompanyForm";
import { EnterpriseContactForm } from "./Enterprise/EnterpriseContactForm";
// import { EnterpriseProjectsForm } from "./Enterprise/EnterpriseProjectsForm";
// import { EnterpriseReviewForm } from "./Enterprise/EnterpriseReviewForm";
import { ProgressSteps } from "./Enterprise/ProgressSteps";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import { API_END_POINTS, ROUTES } from "@/utils/routes";
import { authHeaders, Axios } from "@/utils/axios";
import { currencyCodes, emailRegex, urlRegex } from "@/utils/constant";
import { handleLogout } from "@/utils/logout";

interface EnterPriseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: z.infer<typeof enterpriseSchema>;
}

const enterpriseSchema = z.object({
  userId: z.string().optional(),
  EnterpriseId: z.string().optional(),
  role: z.string().optional(),
  email: z.string().optional(),
  accessToken: z.string().optional(),
  name: z.string().min(2, "Name must be at least 2 characters").optional(),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  industryType: z.string().min(2, "Industry type is required"),
  contactPerson: z.object({
    name: z.string().min(2, "Contact name must be at least 2 characters"),
    email: z.string().regex(emailRegex, "Invalid email format"),
    phone: z
      .string()
      .min(1, "Phone number is required")
      .refine((value) => value && isValidPhoneNumber(value), {
        message: "Invalid phone number format",
      }),
  }),
  location: z.object({
    address: z.string().min(5, "Address must be at least 5 characters"),
    city: z.string().min(2, "City is required"),
    country: z.string().min(2, "Country is required"),
  }),
  // preferredCurrency: z.string().min(3, "Currency is required (e.g., USD)"),
  preferredCurrency: z.enum(currencyCodes, {
    errorMap: () => ({ message: "Please select a valid currency" }),
  }),
  preferredPaymentMethod: z.string().min(2, "Payment method is required"),
  vatOrGstId: z.string().optional(),
  socialMediaProfile: z
    .string()
    .optional()
    .refine((value) => !value || urlRegex.test(value), {
      message: "Invalid URL format",
    }),
  previousProjects: z
    .array(
      z.object({
        projectName: z.string().optional(),
        description: z.string().optional(),
        budget: z.number().min(0, "Budget cannot be negative").optional(),
        completedOn: z
          .date()
          .optional()
          .transform((val) => (val && !isNaN(val.getTime()) ? val : undefined)),
      })
    )
    .optional(),
});

type FormData = z.infer<typeof enterpriseSchema>;

const EnterpriseDetailModal = ({
  isOpen,
  onClose,
  initialData,
}: EnterPriseDetailModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUserStore();
  const { login } = useUserStore();

  const methods = useForm<FormData>({
    resolver: zodResolver(enterpriseSchema),
    defaultValues: {
      EnterpriseId: initialData?.EnterpriseId || "",
      userId: initialData?.userId || "",
      accessToken: initialData?.accessToken || "",
      companyName: "",
      industryType: "",
      contactPerson: { name: "", email: "", phone: "" },
      location: { address: "", city: "", country: "" },
      preferredCurrency: "USD",
      preferredPaymentMethod: "paypal",
      vatOrGstId: "",
      socialMediaProfile: "",
      previousProjects: [],
    },
    mode: "onChange",
  });

  const stepFields = {
    1: [
      "companyName",
      "industryType",
      "preferredCurrency",
      "preferredPaymentMethod",
      "vatOrGstId",
      "socialMediaProfile",
    ],
    2: [
      "contactPerson.name",
      "contactPerson.email",
      "contactPerson.phone",
      "location.address",
      "location.city",
      "location.country",
    ],
    3: [
      "socialMediaProfile",
      "previousProjects.projectName",
      "previousProjects.description",
      "previousProjects.budget",
      "previousProjects.completedOn",
    ],
    4: [],
  };

  const { handleSubmit, trigger, setError, getValues } = methods;

  const handleNext = async () => {
    const isValid = await trigger(stepFields[currentStep] as any);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const onSubmit = async (data: FormData) => {
    const payload = {
      ...data,
      EnterpriseId: initialData?.EnterpriseId || "",
      userId: initialData?.EnterpriseId || data.EnterpriseId,
      accessToken: data?.accessToken ? data?.accessToken : user?.accessToken,
    };
    try {
      const response = await Axios.post(
        `${API_END_POINTS.CREATE_ENTERPRISE_PROFILE}`,
        payload,
        authHeaders(data?.accessToken ? data?.accessToken : user?.accessToken)
      );
      onClose();
      // toast({
      //   title: "Account created successfully!",
      //   description: "Redirecting to dashboard...",
      // });

      // await new Promise((resolve) => setTimeout(resolve, 1500));

      login({
        email: initialData.email,
        name: initialData.name,
        userId: response.data.userId,
        role: initialData.role || data.role,
        isPremium: true,
        accessToken: initialData?.accessToken || response.data.accessToken,
        isDetailsSaved: true,
      });

      localStorage.removeItem("userDataResponse");

      navigate(ROUTES.PROFILE);
      // navigate(ROUTES.DASHBOARD);
    } catch (error) {
      handleLogout();
      console.error("Submission error:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => { }}>
      <DialogContent className="max-w-3xl p-0 rounded-xl overflow-hidden">
        <div className="max-h-[90vh] overflow-y-auto p-6">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-purple-500" />
                Enterprise Profile Setup
              </DialogTitle>
            </div>
            <DialogDescription className="text-gray-600 flex items-center gap-2 mt-1">
              <Calendar className="w-4 h-4" />
              Complete your profile in a few steps
            </DialogDescription>
          </DialogHeader>

          <ProgressSteps currentStep={currentStep} />

          {/* {error && <p className="text-red-500 mb-4">{error}</p>} */}

          <FormProvider {...methods}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (currentStep === 3) {
                  handleSubmit(onSubmit)(e);
                }
              }}
              className="space-y-6"
            >
              {currentStep === 1 && <EnterpriseCompanyForm />}
              {currentStep === 2 && <EnterpriseContactForm />}
              {/* {currentStep === 3 && <EnterpriseProjectsForm />} */}
              {/* {currentStep === 4 && <EnterpriseReviewForm />} */}

              <div className="flex justify-between mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="border-blue-100"
                >
                  Previous
                </Button>
                <div className="flex gap-2">
                  {currentStep < 3 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      {currentStep === 2 ? "Submit" : "Next"}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={methods.formState.isSubmitting}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      {methods.formState.isSubmitting
                        ? "Submitting..."
                        : "Submit"}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnterpriseDetailModal;
